import { parseISO } from 'date-fns';
import { nativeEnum, z, ZodNativeEnum } from 'zod';
import { TranslationKey } from '@/contexts/translations';
import { MAX_ADDRESS_LENGTH, MAX_STATE_LENGTH, PATTERNS, PHONE_MASK_CONFIG } from './constants';
import { unformat } from '@react-input/mask';

export function requiredString(
  message: TranslationKey = 'common.errors.required',
  maxLength?: number | null,
) {
  const zod = z
    .string({ required_error: zt(message), invalid_type_error: zt(message) })
    .trim()
    .min(1, { message });

  return maxLength ? zod.max(maxLength, { message: zt('common.errors.maxLength') }) : zod;
}

export function optionalString(maxLength?: number | null) {
  return maxLength
    ? z
        .string()
        .max(maxLength, { message: zt('common.errors.maxLength') })
        .optional()
    : z.string().optional();
}

export function requiredPhoneNumber(message: TranslationKey = 'common.errors.requiredPhoneNumber') {
  return z
    .string()
    .transform((val) => unformat(val, PHONE_MASK_CONFIG))
    .refine((val) => PATTERNS.phone.test(val), { message });
}

export function optionalPhoneNumber(message: TranslationKey = 'common.errors.invalidPhoneNumber') {
  return z
    .string()
    .optional()
    .transform((val) => (val ? unformat(val, PHONE_MASK_CONFIG) : val))
    .refine((val) => !val || PATTERNS.phone.test(val), { message });
}

export function requiredAddress(message: TranslationKey = 'common.errors.address.address1') {
  return requiredString().max(MAX_ADDRESS_LENGTH, message);
}

export function optionalAddress(message: TranslationKey = 'common.errors.address.address2') {
  return z.string().max(MAX_ADDRESS_LENGTH, zt(message)).optional();
}

// We handle invalid_type_error because react-select can pass undefined before a value is selected,
// causing Zod to throw "Required" instead of our custom message. The min(1) check ensures
// empty strings are caught before the length validation, providing clearer error messages.
export function requiredState(message: TranslationKey = 'common.errors.required') {
  return requiredString(message).length(MAX_STATE_LENGTH, { message: zt(message) });
}

export function optionalZipCode(message: TranslationKey = 'common.errors.address.zipCode') {
  return z.string().refine((val) => !val || PATTERNS.zip.test(val), { message: zt(message) });
}

export function requiredZipCode(message: TranslationKey = 'common.errors.address.zipCode') {
  return requiredString().regex(PATTERNS.zip, message);
}

export function requiredEnum<T extends Record<string, string>>(
  enumObject: T,
  message: TranslationKey = 'common.errors.notSelectedOption',
): ZodNativeEnum<T> {
  return nativeEnum(enumObject, {
    errorMap: () => ({ message }),
  });
}

// This is a helper function to ensure that the error message, passed to zod, is a translation key.
export function zt(message: TranslationKey) {
  return message;
}

export function requiredNumber(message: TranslationKey = 'common.errors.required') {
  return z
    .string()
    .min(1, message)
    .refine((val) => !isNaN(Number(val)), {
      message: zt('common.errors.invalidNumber'),
    });
}

function requiredNumberRange(min: number, max: number, message: TranslationKey) {
  return requiredNumber().refine((val) => Number(val) >= min && Number(val) <= max, {
    message: zt(message),
  });
}

export function heightFeet(message: TranslationKey = 'common.errors.heightFeet') {
  return requiredNumberRange(1, 8, message);
}

export function heightInches(message: TranslationKey = 'common.errors.heightInches') {
  return requiredNumberRange(0, 11, message);
}

export function weightPounds(message: TranslationKey = 'common.errors.weightPounds') {
  return requiredNumberRange(1, 700, message);
}

export interface PasswordRequirement {
  regex: RegExp;
  messageKey: TranslationKey;
}

export const passwordRequirements: PasswordRequirement[] = [
  {
    regex: /^.{8,}$/,
    messageKey: 'register.password.validationRules.length',
  },
  {
    regex: /(?=.*?[A-Z])(?=.*?[a-z]).*/,
    messageKey: 'register.password.validationRules.containsLetters',
  },
  {
    regex: /[0-9]/,
    messageKey: 'register.password.validationRules.containsNumbers',
  },
  {
    regex: /(?=.*?[^A-Za-z0-9]).*/,
    messageKey: 'register.password.validationRules.containsSpecialCharacters',
  },
];

export const isValidPassword = (val: string | undefined | null) => {
  return passwordRequirements.every((requirement) => requirement.regex.test(val || ''));
};

export const isValidCode = (val: string | undefined | null) => {
  return /^[0-9]{6}$/.test(val ?? '');
};

export function isPastDate(value: string) {
  const targetDate = parseISO(value);
  targetDate.setHours(0, 0, 0, 0);

  return targetDate <= new Date();
}

export function requiredPastDate(message: TranslationKey = 'common.errors.pastDate') {
  return requiredString().refine(isPastDate, { message: zt(message) });
}

export function optionalPastDate(message: TranslationKey = 'common.errors.pastDate') {
  return z
    .string()
    .optional()
    .refine((val) => !val || isPastDate(val), { message: zt(message) });
}
